import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { 
  MessageSquare, User, Shield, AlertTriangle, Star, 
  Upload, Send, ChevronDown, FileText,
  CheckCircle, Calendar, MapPin, Lock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Set up axios to always send the auth token
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("apiToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Shared animation presets to avoid inline object creation on every render
const FADE_IN = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };
const FADE_IN_DELAY = (delay) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay } });
const SCALE_IN = { initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 } };

const SEVERITY_STYLES = {
  4: 'bg-red-500/20 border-red-500/50 text-red-400',
  3: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
  2: 'bg-amber-500/20 border-amber-500/50 text-amber-400',
  1: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
};
const getSeverityStyle = (level) => SEVERITY_STYLES[level] || SEVERITY_STYLES[1];


const SEVERITY_LABELS = {
  1: "Minor concern",
  2: "Moderate issue",
  3: "Serious issue",
  4: "Major misconduct"
};

const RANK_DISPLAY = {
  "HEAD ADMIN": "Command",
  "SENIOR ADMIN": "Senior Administration",
  "ADMIN": "Administration",
  "SENIOR MOD": "Senior Moderators",
  "MOD": "Moderators",
  "JRMOD": "Junior Moderators"
};

const StarRating = ({ rating, setRating, labels: customLabels }) => {
  const [hover, setHover] = useState(0);
  const labels = customLabels || ["Very Poor", "Below Average", "Neutral", "Good", "Excellent"];
  
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                star <= (hover || rating)
                  ? "text-yellow-400 fill-yellow-400"
                  : "text-zinc-600"
              }`}
            />
          </button>
        ))}
      </div>
      <p className={`text-sm ${rating >= 4 ? 'text-emerald-400' : rating >= 3 ? 'text-yellow-400' : 'text-red-400'}`}>
        {labels[rating - 1] || "Select a rating"}
      </p>
    </div>
  );
};

// Discord Login Gate
const DiscordLoginGate = ({ onAuthenticated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDiscordCallback = useCallback(async (code) => {
    setLoading(true);
    setError("");
    try {
      const redirectUri = `${window.location.origin}/feedback`;
      const res = await axios.post(`${API}/auth/discord/callback`, {
        code,
        redirect_uri: redirectUri
      });
      
      // Store Discord user info in sessionStorage
      sessionStorage.setItem("discord_user", JSON.stringify(res.data));
      if (res.data.token) {
        sessionStorage.setItem("apiToken", res.data.token);
      }
      
      // Clean URL
      window.history.replaceState({}, document.title, "/feedback");
      
      onAuthenticated(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Discord authentication failed";
      setError(detail);
      // Clean URL on error too
      window.history.replaceState({}, document.title, "/feedback");
    } finally {
      setLoading(false);
    }
  }, [onAuthenticated]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (code) {
      handleDiscordCallback(code);
    }
  }, [handleDiscordCallback]);

  const handleDiscordLogin = async () => {
    setLoading(true);
    try {
      const redirectUri = `${window.location.origin}/feedback`;
      const res = await axios.get(`${API}/auth/discord/url`, {
        params: { redirect_uri: redirectUri }
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError("Failed to start Discord login");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        {...FADE_IN}
        className="glass-card p-8 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 bg-[#5865F2]/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z"/>
          </svg>
        </div>
        
        <h2 className="text-2xl font-heading font-bold text-white mb-3">
          Staff Feedback Form
        </h2>
        <p className="text-zinc-400 mb-6">
          Please verify your identity with Discord before submitting feedback.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-none text-red-400 text-sm" data-testid="discord-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-3 py-4">
            <div className="w-5 h-5 border-2 border-[#5865F2]/30 border-t-[#5865F2] rounded-full animate-spin" />
            <span className="text-zinc-400">Authenticating with Discord...</span>
          </div>
        ) : (
          <button
            onClick={handleDiscordLogin}
            className="w-full py-3 px-6 bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold rounded-none transition-colors flex items-center justify-center gap-3"
            data-testid="discord-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z"/>
            </svg>
            Login with Discord
          </button>
        )}

        <p className="text-zinc-600 text-xs mt-4">
          Union Interactive Staff Standards
        </p>
      </motion.div>
    </div>
  );
};

const FeedbackForm = () => {
  const [gateUnlocked, setGateUnlocked] = useState(() => {
    return sessionStorage.getItem("gateUnlocked") === "true";
  });
  const [gatePassword, setGatePassword] = useState("");
  const [gateError, setGateError] = useState("");
  const [gateLoading, setGateLoading] = useState(false);

  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateLoading(true);
    setGateError("");
    try {
      const res = await axios.post(`${API}/auth/gate`, { password: gatePassword });
      if (res.data.success) {
        sessionStorage.setItem("gateUnlocked", "true");
        setGateUnlocked(true);
      }
    } catch (err) {
      setGateError("Incorrect password");
    }
    setGateLoading(false);
  };

  const [discordUser, setDiscordUser] = useState(() => {
    const stored = sessionStorage.getItem("discord_user");
    return stored ? JSON.parse(stored) : null;
  });
  
  const [staffList, setStaffList] = useState({});
  const [formConfig, setFormConfig] = useState(null);
  const [customAnswers, setCustomAnswers] = useState({});
  const [formData, setFormData] = useState({
    reporter_discord: "",
    reporter_roblox: "",
    is_anonymous: false,
    staff_username: "",
    staff_rank: "",
    feedback_type: "",
    severity: 1,
    description: "",
    incident_date: "",
    incident_location: "",
    has_evidence: false,
    evidence_links: [""],
    star_rating: 3,
    suggestions: ""
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Get current type config from form config
  const currentTypeConfig = formConfig?.feedback_types?.find(t => t.name === formData.feedback_type);
  const hideSeverity = currentTypeConfig?.hide_severity || false;
  const hideIncident = currentTypeConfig?.hide_incident || false;
  const typeQuestions = currentTypeConfig?.questions || [];

  // Load form config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/form-config`);
        setFormConfig(res.data);
      } catch (err) {
        toast.error("Failed to load form config");
      }
    };
    fetchConfig();
  }, []);

  // Load staff list from API
  useEffect(() => {
    const fetchStaffList = async () => {
      try {
        const res = await axios.get(`${API}/staff-list-for-feedback`);
        setStaffList(res.data);
      } catch (err) {
        toast.error("Failed to load staff list");
      }
    };
    if (discordUser) {
      fetchStaffList();
    }
  }, [discordUser]);

  // Pre-fill Discord username when authenticated
  useEffect(() => {
    if (discordUser) {
      setFormData(prev => ({
        ...prev,
        reporter_discord: discordUser.discord_username || ""
      }));
    }
  }, [discordUser]);

  // Reset custom answers when feedback type changes
  useEffect(() => {
    setCustomAnswers({});
  }, [formData.feedback_type]);

  // If not authenticated, show Discord login
  if (!discordUser) {
    return <DiscordLoginGate onAuthenticated={setDiscordUser} />;
  }

  const handleStaffSelect = (username, rank) => {
    setFormData({ ...formData, staff_username: username, staff_rank: rank });
  };

  const handleEvidenceLinkChange = (index, value) => {
    const newLinks = [...formData.evidence_links];
    newLinks[index] = value;
    setFormData({ ...formData, evidence_links: newLinks });
  };

  const addEvidenceLink = () => {
    setFormData({ ...formData, evidence_links: [...formData.evidence_links, ""] });
  };

  const removeEvidenceLink = (index) => {
    const newLinks = formData.evidence_links.filter((_, i) => i !== index);
    setFormData({ ...formData, evidence_links: newLinks.length ? newLinks : [""] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reporter_roblox.trim() && !formData.reporter_discord.trim()) {
      toast.error("Please enter your Roblox or Discord username");
      return;
    }
    if (!formData.staff_username) {
      toast.error("Please select a staff member");
      return;
    }
    if (!formData.feedback_type) {
      toast.error("Please select a feedback type");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please describe what happened");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        is_anonymous: false,
        evidence_links: formData.evidence_links.filter(link => link.trim()),
        custom_answers: typeQuestions.map((q) => ({
          question: q.label,
          answer: customAnswers[q.label] || ""
        })).filter(qa => qa.answer.trim())
      };
      
      await axios.post(`${API}/feedback-reports`, submitData);
      toast.success("Report submitted successfully!");
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to submit report. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("discord_user");
    setDiscordUser(null);
  };

  if (submitted) {
    return (
      <div className="min-h-[100dvh] bg-slate-950 flex items-center justify-center p-4">
        <motion.div
          {...SCALE_IN}
          className="glass-card p-8 max-w-md text-center"
        >
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-2">Report Submitted</h2>
          <p className="text-zinc-400 mb-6">
            Thank you for your feedback. Staff Standards Command will review your report.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                reporter_discord: discordUser?.discord_username || "",
                reporter_roblox: "",
                is_anonymous: false,
                staff_username: "",
                staff_rank: "",
                feedback_type: "",
                severity: 1,
                description: "",
                incident_date: "",
                incident_location: "",
                has_evidence: false,
                evidence_links: [""],
                star_rating: 3,
                suggestions: ""
              });
              setCustomAnswers({});
            }}
            className="btn-primary"
            data-testid="submit-another-btn"
          >
            Submit Another Report
          </button>
        </motion.div>
      </div>
    );
  }

  if (!gateUnlocked) {
    return (
      <div className="min-h-[100dvh] bg-slate-950 flex items-center justify-center p-4">
        <motion.div {...SCALE_IN} className="w-full max-w-md">
          <div className="bg-slate-900/80 border border-zinc-700/50 rounded-2xl p-8 text-center">
            <div className="p-4 rounded-none bg-red-500/20 border border-red-500/30 w-fit mx-auto mb-6">
              <Lock className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">ACCESS RESTRICTED</h1>
            <p className="text-zinc-400 text-sm mb-6">Enter the site password to continue</p>
            <form onSubmit={handleGateSubmit} className="space-y-4">
              <input type="password" value={gatePassword} onChange={(e) => setGatePassword(e.target.value)}
                placeholder="Enter password" className="w-full px-4 py-3 bg-zinc-800/50 border border-slate-600/50 rounded-none text-white text-center text-lg tracking-widest" autoFocus />
              {gateError && <p className="text-red-400 text-sm">{gateError}</p>}
              <button type="submit" disabled={gateLoading || !gatePassword}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 rounded-none font-semibold text-white transition-colors">
                {gateLoading ? "Verifying..." : "UNLOCK"}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-slate-950 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          {...FADE_IN}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 rounded-none bg-blue-500/20 border border-blue-500/30">
              <MessageSquare className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">
            {formConfig?.form_title || "Staff Feedback & Conduct Report Form"}
          </h1>
          <div className="max-w-2xl mx-auto space-y-2">
            <p className="text-zinc-400">
              {formConfig?.form_description || "This form allows community members to provide feedback about staff performance."}
            </p>
            <p className="text-zinc-400">
              {formConfig?.form_subtitle || "All reports are reviewed by Staff Standards Command."}
            </p>
            <p className="text-amber-400 text-sm mt-4">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              {formConfig?.warning_text || "False reports or abuse of this form may result in moderation action."}
            </p>
          </div>
        </motion.div>

        {/* Discord User Badge */}
        <motion.div
          {...FADE_IN}
          transition={{ delay: 0.05 }}
          className="glass-card p-4 mb-6 border border-[#5865F2]/30 bg-[#5865F2]/5"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {discordUser.avatar_url ? (
                <img src={discordUser.avatar_url} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#5865F2]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#5865F2]" />
                </div>
              )}
              <div>
                <p className="text-white font-medium">{discordUser.discord_global_name || discordUser.discord_username}</p>
                <p className="text-zinc-500 text-sm">@{discordUser.discord_username}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
              data-testid="discord-logout-btn"
            >
              Sign out
            </button>
          </div>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Reporter Information */}
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-heading font-bold text-white">Your Information *</h2>
            </div>

            <div className="space-y-4">
              <p className="text-zinc-500 text-sm">
                Your Discord is verified. Please also provide your Roblox username.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Roblox Username *</label>
                  <input
                    type="text"
                    value={formData.reporter_roblox}
                    onChange={(e) => setFormData({ ...formData, reporter_roblox: e.target.value })}
                    placeholder="Your Roblox username"
                    className="input-field w-full"
                    data-testid="reporter-roblox-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Discord Username (verified)</label>
                  <input
                    type="text"
                    value={formData.reporter_discord}
                    readOnly
                    className="input-field w-full opacity-60 cursor-not-allowed"
                    data-testid="reporter-discord-input"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Staff Member (dynamic from API) */}
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.15 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-heading font-bold text-white">Staff Member *</h2>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-zinc-400 mb-2">Select the staff member (Roblox Username)</label>
              <select
                value={formData.staff_username}
                onChange={(e) => {
                  const selected = e.target.value;
                  let rank = "";
                  Object.entries(staffList).forEach(([rankKey, members]) => {
                    members.forEach(m => {
                      if (m.username === selected) rank = RANK_DISPLAY[rankKey] || rankKey;
                    });
                  });
                  handleStaffSelect(selected, rank);
                }}
                className="select-field w-full"
                required
                data-testid="staff-select"
              >
                <option value="">Select a staff member...</option>
                {Object.entries(staffList).map(([rankKey, members]) => {
                  if (members.length === 0) return null;
                  return (
                    <optgroup key={rankKey} label={`— ${RANK_DISPLAY[rankKey] || rankKey} —`}>
                      {members.map((member) => (
                        <option key={member.username} value={member.username}>
                          {member.name} {member.role_title ? `(${member.role_title})` : `— ${RANK_DISPLAY[rankKey] || rankKey}`}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>
          </motion.div>

          {/* Section 3: Type of Feedback */}
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-heading font-bold text-white">Type of Feedback *</h2>
            </div>

            <select
              value={formData.feedback_type}
              onChange={(e) => setFormData({ ...formData, feedback_type: e.target.value })}
              className="select-field w-full"
              required
              data-testid="feedback-type-select"
            >
              <option value="">Select feedback type...</option>
              {(formConfig?.feedback_types || []).map((type) => (
                <option key={type.name} value={type.name}>{type.name}</option>
              ))}
            </select>
          </motion.div>

          {/* Section 4: Report Severity (not for positive/poor feedback) */}
          {formData.feedback_type && !hideSeverity && (
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.25 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              <h2 className="text-lg font-heading font-bold text-white">Report Severity *</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, severity: level })}
                  className={`p-4 rounded-none border text-left transition-all ${
                    formData.severity === level ? getSeverityStyle(level) : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-slate-600'
                  }`}
                  data-testid={`severity-btn-${level}`}
                >
                  <span className="text-2xl font-bold">{level}</span>
                  <span className="ml-2 text-sm">— {SEVERITY_LABELS[level]}</span>
                </button>
              ))}
            </div>
          </motion.div>
          )}

          {/* Section 5: Incident Details / Assessment */}
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.3 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-heading font-bold text-white">
                {currentTypeConfig?.description_label ? currentTypeConfig.description_label.replace("?", "") + " Details" : "Incident Details"}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  {currentTypeConfig?.description_label || "What happened?"} *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={currentTypeConfig?.description_placeholder || "Please describe in detail..."}
                  className="input-field w-full h-32 resize-none"
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Dynamic Questions from config */}
              {typeQuestions.length > 0 && (
                <div className="space-y-4 border-t border-zinc-700 pt-4">
                  {typeQuestions.map((q) => (
                    <div key={q.label}>
                      <label className="block text-sm text-zinc-400 mb-2">{q.label}</label>
                      <textarea
                        value={customAnswers[q.label] || ""}
                        onChange={(e) => setCustomAnswers({ ...customAnswers, [q.label]: e.target.value })}
                        placeholder={q.placeholder || "Enter your answer..."}
                        className="input-field w-full h-20 resize-none"
                        data-testid={`custom-question-${q.label}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {!hideIncident && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    When did this happen?
                  </label>
                  <input
                    type="text"
                    value={formData.incident_date}
                    onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                    placeholder="March 4th 2026 — 8:30PM EST"
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Where did this happen?
                  </label>
                  <select
                    value={formData.incident_location}
                    onChange={(e) => setFormData({ ...formData, incident_location: e.target.value })}
                    className="select-field w-full"
                  >
                    <option value="">Select location...</option>
                    {(formConfig?.incident_locations || []).map((loc) => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
              </div>
              )}
            </div>
          </motion.div>

          {/* Section 6: Evidence */}
          {formConfig?.evidence_enabled !== false && (
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.35 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Upload className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-heading font-bold text-white">{formConfig?.evidence_label || "Evidence"}</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <p className="text-zinc-400">Do you have proof?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, has_evidence: true })}
                    className={`px-4 py-2 rounded-none transition-colors ${
                      formData.has_evidence
                        ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-400'
                        : 'bg-zinc-800 border border-zinc-700 text-zinc-400'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, has_evidence: false })}
                    className={`px-4 py-2 rounded-none transition-colors ${
                      !formData.has_evidence
                        ? 'bg-slate-600/50 border border-slate-500/50 text-zinc-300'
                        : 'bg-zinc-800 border border-zinc-700 text-zinc-400'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {formData.has_evidence && (
                <div className="space-y-3">
                  <p className="text-sm text-zinc-500">
                    {formConfig?.evidence_description || "Add links to screenshots, video clips, chat logs, or moderation logs"}
                  </p>
                  {formData.evidence_links.map((link, index) => (
                    <div key={`evidence-${index}-${formData.evidence_links.length}`} className="flex gap-2">
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => handleEvidenceLinkChange(index, e.target.value)}
                        placeholder="https://imgur.com/... or paste link"
                        className="input-field flex-1"
                      />
                      {formData.evidence_links.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEvidenceLink(index)}
                          className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-none text-red-400 hover:bg-red-500/30"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addEvidenceLink}
                    className="text-sm text-cyan-400 hover:text-cyan-300"
                  >
                    + Add another link
                  </button>
                </div>
              )}
            </div>
          </motion.div>
          )}

          {/* Section 7: Staff Rating */}
          {formConfig?.star_rating_enabled !== false && (
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.4 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-heading font-bold text-white">Staff Rating</h2>
            </div>

            <StarRating
              rating={formData.star_rating}
              setRating={(rating) => setFormData({ ...formData, star_rating: rating })}
              labels={formConfig?.star_rating_labels}
            />
          </motion.div>
          )}

          {/* Section 8: Suggestions */}
          {formConfig?.suggestions_enabled !== false && (
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.45 }}
            className="glass-card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-heading font-bold text-white">{formConfig?.suggestions_label || "Optional Suggestions"}</h2>
            </div>

            <textarea
              value={formData.suggestions}
              onChange={(e) => setFormData({ ...formData, suggestions: e.target.value })}
              placeholder={formConfig?.suggestions_placeholder || "What could the staff member have done better?"}
              className="input-field w-full h-24 resize-none"
            />
          </motion.div>
          )}

          {/* Submit Button */}
          <motion.div
            {...FADE_IN}
            transition={{ delay: 0.5 }}
          >
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold rounded-none transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="submit-report-btn"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit Report
                </>
              )}
            </button>
          </motion.div>
        </form>

        {/* Footer */}
        <motion.footer
          {...FADE_IN_DELAY(0.6)}
          className="mt-8 text-center text-zinc-600 text-sm"
        >
          <p>Union Interactive Staff Standards - All reports are confidential</p>
        </motion.footer>
      </div>
    </div>
  );
};

export default FeedbackForm;
